import React, { useState, useEffect, useCallback } from "react";
import { listPlayerProfiles, PlayerProfile } from "../store/supabasePlayerProfiles";
import { Team, SquadFormat, listTeams, createTeam, updateTeam, deleteTeam, getSquadWithPlayers } from "../store/teams";
import "./DirectoryPages.css";
import { useAuth } from "../contexts/AuthContext";
import TeamSquadManager from "../components/team/TeamSquadManager";
import { TeamLogoUpload } from "../components/TeamLogoUpload";

interface TeamsProps {
  format: SquadFormat;
  onClose: () => void;
  autoCreate?: boolean;
}

const FORMAT_LABELS: Record<SquadFormat, string> = {
  indoor: "Indoor",
  t20: "T20",
  odi: "ODI",
};

const Teams: React.FC<TeamsProps> = ({ format, onClose, autoCreate }) => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [profiles, setProfiles] = useState<PlayerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [activeFormat, setActiveFormat] = useState<SquadFormat>(format);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [squadMeta, setSquadMeta] = useState<Record<string, { count: number; captain: string | null; viceCaptain: string | null }>>({});

  // Open create-team modal automatically when autoCreate is set
  useEffect(() => {
    if (autoCreate && !loading && user && teams.length >= 0 && !selectedTeam && !editingTeam) {
      setShowCreate(true);
    }
  }, [autoCreate, loading, user, teams.length, selectedTeam, editingTeam]);

  const formatLabel = FORMAT_LABELS[activeFormat];

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const [teamList, profileList] = await Promise.all([listTeams(), listPlayerProfiles()]);
      const squads = await Promise.all(teamList.map(async (team) => ({ team, squad: await getSquadWithPlayers(team.id, team.format) })));
      const profileNames = new Map(profileList.map((profile) => [profile.id, profile.name]));
      const meta: Record<string, { count: number; captain: string | null; viceCaptain: string | null }> = {};
      squads.forEach(({ team, squad }) => {
        meta[team.id] = {
          count: squad?.players.length || 0,
          captain: squad?.squad.captain_player_id ? profileNames.get(squad.squad.captain_player_id) || null : null,
          viceCaptain: squad?.squad.vice_captain_player_id ? profileNames.get(squad.squad.vice_captain_player_id) || null : null,
        };
      });
      setTeams(teamList);
      setProfiles(Array.isArray(profileList) ? profileList : []);
      setSquadMeta(meta);
    } catch (err: any) {
      setError(err?.message || "Failed to load teams");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedTeam(null);
    setActiveFormat(format);
  }, [format]);

  async function handleDelete(team: Team) {
    if (!window.confirm(`Delete "${team.name}"? This removes all its squads. This cannot be undone.`)) {
      return;
    }
    try {
      await deleteTeam(team.id);
      setTeams((prev) => prev.filter((t) => t.id !== team.id));
      if (selectedTeam?.id === team.id) setSelectedTeam(null);
    } catch (err: any) {
      setError(err?.message || "Failed to delete team");
    }
  }

  const shell = (children: React.ReactNode, headerRight?: React.ReactNode, onBack?: () => void) => (
    <div className="min-h-screen bg-gradient-to-br from-green-950 via-green-900 to-green-950 text-white flex flex-col pb-20 lg:pb-8">
      <header className="bg-green-900/50 backdrop-blur-sm py-4 shadow-lg border-b border-green-800/50 sticky top-0 z-30">
        <div className="max-w-4xl lg:max-w-6xl mx-auto px-4 lg:px-8 flex items-center justify-between gap-3">
          <button
            onClick={onBack || onClose}
            className="text-green-300 hover:text-white p-2 hover:bg-green-800/50 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-white truncate">
            {selectedTeam ? selectedTeam.name : `${formatLabel} Teams`}
          </h1>
          <div className="min-w-16 flex justify-end">{headerRight}</div>
        </div>
      </header>
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-4xl lg:max-w-6xl mx-auto px-4 lg:px-8 py-6">{children}</div>
      </main>
    </div>
  );

  const visibleTeams = teams.filter((team) => team.format === activeFormat);

  if (loading) return <div className="ww-directory ww-directory--loading">Loading teams...</div>;

  if (!user) {
    return <div className="ww-directory ww-directory--loading">Please sign in to manage teams.</div>;
  }

  if (selectedTeam) {
    return shell(
      <div className="space-y-4"><TeamSquadManager team={selectedTeam} profiles={profiles} /></div>,
      <button onClick={() => setEditingTeam(selectedTeam)} className="bg-green-800/60 hover:bg-green-700 px-3 py-2 rounded-lg text-sm font-semibold transition-colors">Edit</button>,
      () => setSelectedTeam(null)
    );
  }

  return (
    <div className="ww-directory">
      <main className="ww-directory__content">
        <div className="ww-directory__heading"><div><h1>Teams</h1><p>View and manage your teams and squads.</p></div><button className="ww-directory__primary" onClick={() => setShowCreate(true)}>+ New Team</button></div>
        {error && <div className="ww-directory__empty"><p>{error}</p><button className="ww-directory__primary" onClick={load}>Retry</button></div>}
        <div className="ww-directory__tabs" role="tablist" aria-label="Team format filters">
          {(['indoor', 't20', 'odi'] as SquadFormat[]).map((filter) => { const count = teams.filter((team) => team.format === filter).length; return <button key={filter} role="tab" aria-selected={activeFormat === filter} className={activeFormat === filter ? "is-active" : ""} onClick={() => setActiveFormat(filter)}>{FORMAT_LABELS[filter]} <span>({count})</span></button>; })}
        </div>
        {visibleTeams.length === 0 ? (
          <div className="ww-directory__empty"><h2>No {FORMAT_LABELS[activeFormat]} teams yet</h2><p>Create a team and build a squad you can reuse across matches.</p><button className="ww-directory__primary" onClick={() => setShowCreate(true)}>Create {FORMAT_LABELS[activeFormat]} Team</button></div>
        ) : (
          <div className="ww-directory__list">
            {visibleTeams.map((team) => { const meta = squadMeta[team.id] || { count: 0, captain: null, viceCaptain: null }; return <article key={team.id} className="ww-directory-card" onClick={() => setSelectedTeam(team)}>
              {team.logo_url ? <img src={team.logo_url} alt={`${team.name} logo`} className="ww-team-card__logo" /> : <div className="ww-team-card__fallback" aria-hidden="true">{team.name.slice(0, 2).toUpperCase()}</div>}
              <div className="ww-directory-card__body"><h2>{team.name}</h2><div className="ww-team-card__meta"><span>♟ {meta.count} player{meta.count === 1 ? "" : "s"}</span><span>Captain: {meta.captain || "—"}</span><span>Vice-Captain: {meta.viceCaptain || "—"}</span></div></div>
              <div className="ww-directory-card__actions"><button aria-label={`Actions for ${team.name}`} onClick={(event) => { event.stopPropagation(); setOpenMenu(openMenu === team.id ? null : team.id); }}>⋮</button>{openMenu === team.id && <div className="ww-directory-menu"><button onClick={(event) => { event.stopPropagation(); setEditingTeam(team); setOpenMenu(null); }}>Edit</button><button className="is-danger" onClick={(event) => { event.stopPropagation(); setOpenMenu(null); handleDelete(team); }}>Delete</button></div>}<button aria-label={`Open ${team.name}`} onClick={(event) => { event.stopPropagation(); setSelectedTeam(team); }}>›</button></div>
            </article>; })}
          </div>
        )}
      </main>
      {(showCreate || editingTeam) && <TeamFormModal team={editingTeam} format={activeFormat} onClose={() => { setShowCreate(false); setEditingTeam(null); }} onSaved={(saved) => { setTeams((prev) => { const exists = prev.some((team) => team.id === saved.id); return exists ? prev.map((team) => team.id === saved.id ? saved : team) : [saved, ...prev]; }); setShowCreate(false); setEditingTeam(null); }} />}
    </div>
  );
};

interface TeamFormModalProps {
  team: Team | null;
  format: SquadFormat;
  onClose: () => void;
  onSaved: (team: Team) => void;
}

const TeamFormModal: React.FC<TeamFormModalProps> = ({ team, format, onClose, onSaved }) => {
  const [name, setName] = useState(team?.name || "");
  const [logoUrl, setLogoUrl] = useState<string | null>(team?.logo_url ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatLabel = FORMAT_LABELS[format];

  async function handleSave() {
    if (!name.trim()) {
      setError("Team name is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = team
        ? await updateTeam(team.id, { name, logo_url: logoUrl })
        : await createTeam(name, format, logoUrl);
      onSaved(saved);
    } catch (err: any) {
      setError(err?.message || "Failed to save team");
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-green-900 rounded-xl max-w-md w-full border border-green-800">
        <div className="border-b border-green-800 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">{team ? "Rename Team" : `New ${formatLabel} Team`}</h2>
          <button onClick={onClose} className="text-2xl hover:bg-green-800 rounded-lg p-2 transition-colors">
            &times;
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-900/40 border border-red-700/50 rounded-lg p-3 text-red-200 text-sm">
              {error}
            </div>
          )}
          {!team && (
            <div className="bg-green-800/30 border border-green-700/50 rounded-lg px-4 py-2 text-green-300 text-sm">
              Format: <span className="font-bold text-white">{formatLabel}</span>
            </div>
          )}
          <div className="flex justify-center pt-2">
            <TeamLogoUpload
              team={team ? team.id : "new"}
              teamName={name}
              logoUrl={logoUrl}
              onLogoChange={setLogoUrl}
              hideNameLabel
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Team Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
              placeholder="Enter team name"
              className="w-full bg-green-950 border border-green-800 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              autoFocus
            />
          </div>
          <div className="flex gap-4 pt-2">
            <button
              onClick={onClose}
              className="flex-1 bg-green-800 hover:bg-green-700 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!name.trim() || saving}
              className="flex-1 bg-green-600 hover:bg-green-500 disabled:bg-green-900 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              {saving ? "Saving..." : team ? "Save" : "Create Team"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Teams;
